import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Circle, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { errMsg, formatDate, useApi } from "@/lib/api";

const WHAT = {
  bucket: "Crea il bucket R2 «{bucket}» dove vengono salvati i file dei siti.",
  kv: "Crea l'archivio KV «{kv_title}» con gli indirizzi dei siti.",
  worker: "Carica (o aggiorna) il Worker «{worker}» che serve i siti leggendo KV e R2.",
  route: "Collega i sottodomini {route} al Worker. I record già esistenti (es. www) continuano a raggiungere il loro server.",
};

export default function SetupSteps({ h, onChange }) {
  const api = useApi();
  const [busy, setBusy] = useState("");
  const [ask, setAsk] = useState(null);
  const fill = (s) => s.replace("{bucket}", h.bucket).replace("{kv_title}", h.kv_title).replace("{worker}", h.worker).replace("{route}", h.derived?.route_pattern || "");
  const missing = h.steps.filter((s) => !s.done_at || (s.key === "worker" && h.worker_outdated)).map((s) => s.key);
  const run = async (keys) => {
    setAsk(null);
    for (const k of keys) {
      setBusy(k);
      try { const { data } = await api.post(`/hosting/setup/${k}`, { confirm: true }); onChange(data); }
      catch (e) { toast.error(errMsg(e)); setBusy(""); return; }
    }
    setBusy("");
    toast.success(keys.length > 1 ? "Passaggi completati" : "Passaggio completato");
  };
  const cf = h.mode === "cloudflare";
  return (
    <div className="space-y-3" data-testid="hosting-steps">
      <ol className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        {h.steps.map((s, i) => {
          const outdated = s.key === "worker" && h.worker_outdated;
          return (
            <li key={s.key} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`hosting-step-${s.key}`}>
              {busy === s.key ? <Loader2 size={18} className="animate-spin text-sky-600" /> : s.done_at && !outdated ? <CheckCircle2 size={18} className="text-emerald-600" /> : <Circle size={18} className="text-slate-300" />}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900">{i + 1}. {s.label}</p>
                <p className="text-xs text-slate-500">{fill(WHAT[s.key])}</p>
                <p className={`text-[11px] ${outdated ? "text-amber-700" : "text-slate-400"}`} data-testid={`hosting-step-state-${s.key}`}>{outdated ? "Il codice del Worker è cambiato: aggiornalo" : s.done_at ? `Fatto ${formatDate(s.done_at, true)}` : "Da fare"}</p>
              </div>
              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => setAsk([s.key])} data-testid={`hosting-step-run-${s.key}`}>{outdated ? <><RefreshCw size={13} className="mr-1" />Aggiorna</> : s.done_at ? <><RefreshCw size={13} className="mr-1" />Ripeti</> : "Esegui"}</Button>
            </li>
          );
        })}
      </ol>
      {missing.length > 0 && <div className="flex justify-end"><Button disabled={!!busy} onClick={() => setAsk(missing)} className="bg-sky-600 hover:bg-sky-700" data-testid="hosting-steps-run-missing">Esegui i passaggi mancanti ({missing.length})</Button></div>}
      <ConfirmDialog open={!!ask} onOpenChange={(o) => !o && setAsk(null)} onConfirm={() => run(ask)} confirmLabel={cf ? "Sì, modifica il mio account" : "Esegui"}
        title={cf ? "Modificare il tuo account Cloudflare?" : "Eseguire sull'emulatore locale?"}
        description={`${(ask || []).map((k) => fill(WHAT[k])).join(" ")} ${cf ? "Le risorse vengono create sul tuo account Cloudflare (rientrano nei piani gratuiti di Workers, R2 e KV entro i loro limiti)." : "Nessun account, nessun costo."}`} />
    </div>
  );
}
